import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileClock, WifiOff, AlertTriangle, Activity, RefreshCw,
  ArrowUpRight, Building2, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { Input } from "@/components/ui/input";

const TABS = [
  { k: "health", label: "Garage Health" },
  { k: "events", label: "Recent Sync Events" },
];

const Kpi = ({ Icon, label, value, tone = "steel" }) => {
  const map = {
    steel: "bg-[#a8dadc]/30 text-[#457b9d]",
    red: "bg-[#e63946]/10 text-[#e63946]",
    orange: "bg-[#F79009]/10 text-[#F79009]",
    green: "bg-[#12B76A]/10 text-[#12B76A]",
  };
  return (
    <div className="bg-white rounded-xl border border-[#E4E7EC] p-4">
      <div className={`w-9 h-9 rounded-lg grid place-items-center mb-3 ${map[tone]}`}>
        <Icon size={17} strokeWidth={2} />
      </div>
      <div className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">{label}</div>
      <div className="text-[26px] md:text-[30px] font-bold text-[#1d3557] leading-tight mt-1 font-mono">{value}</div>
    </div>
  );
};

export default function AdminSyncMonitor() {
  const [tab, setTab] = useState("health");
  const [health, setHealth] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/support/sync-health").then((r) => setHealth(r.data || [])).catch(() => {}),
      api.get("/admin/support/sync-events").then((r) => setEvents(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filteredHealth = useMemo(() => {
    if (!q.trim()) return health;
    const s = q.toLowerCase();
    return health.filter((g) => g.garage_name?.toLowerCase().includes(s) || (g.city || "").toLowerCase().includes(s));
  }, [health, q]);
  const filteredEvents = useMemo(() => {
    if (!q.trim()) return events;
    const s = q.toLowerCase();
    return events.filter((e) => (e.garage_name || "").toLowerCase().includes(s) || (e.kind || "").toLowerCase().includes(s) || (e.detail || "").toLowerCase().includes(s));
  }, [events, q]);

  const totals = useMemo(() => {
    return {
      garages: health.length,
      dormant: health.filter((g) => g.dormant_30d).length,
      stale: health.filter((g) => g.stale).length,
      events7d: health.reduce((a, g) => a + (g.sync_events_7d || 0), 0),
    };
  }, [health]);

  return (
    <div className="space-y-5" data-testid="admin-sync-monitor-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Support</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <FileClock size={22} className="text-[#457b9d]" /> Sync Monitor
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            Per-garage adoption + offline queue health. Tech Ops & Support use this to catch stuck workshops.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="admin-sync-refresh"
          className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[13px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi Icon={Building2} label="Garages tracked"      value={totals.garages} />
        <Kpi Icon={AlertTriangle} label="Stale (>14d)"    value={totals.stale}    tone="orange" />
        <Kpi Icon={WifiOff} label="Dormant (>30d)"        value={totals.dormant}  tone="red" />
        <Kpi Icon={Activity} label="Sync events 7d"       value={totals.events7d} tone="green" />
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E4E7EC]">
        <div className="inline-flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-sync-tab-${t.k}`}
              className={`h-10 px-4 text-[13px] font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.k
                  ? "border-[#e63946] text-[#1d3557]"
                  : "border-transparent text-[#667085] hover:text-[#1d3557]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <Input
        data-testid="admin-sync-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={tab === "health" ? "Search garage or city…" : "Search garage or event kind…"}
        className="max-w-md h-9 rounded-md text-[13px]"
      />

      {tab === "health" && (
        <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-sync-health-table">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Garage</th>
                <th className="text-left px-4 py-3 font-semibold">Last activity</th>
                <th className="text-right px-4 py-3 font-semibold">JC / week</th>
                <th className="text-right px-4 py-3 font-semibold">Sync events 7d</th>
                <th className="text-left px-4 py-3 font-semibold">Flags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1FAEE]">
              {loading && <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">Loading…</td></tr>}
              {!loading && filteredHealth.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-[13px] text-[#667085]">No garages match the filter.</td></tr>
              )}
              {filteredHealth.map((g) => (
                <tr key={g.garage_id} className="hover:bg-[#F7F9FC]" data-testid={`admin-sync-health-row-${g.garage_id}`}>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/garages/${g.garage_id}`}
                      className="inline-flex items-center gap-2 text-[#1d3557] hover:text-[#e63946] font-semibold"
                    >
                      <Building2 size={13} className="text-[#457b9d]" /> {g.garage_name}
                      <ArrowUpRight size={11} className="text-[#a8dadc]" />
                    </Link>
                    <div className="text-[11px] text-[#667085] mt-0.5">{g.city || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-[11px] font-mono text-[#667085] whitespace-nowrap">
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {g.last_activity ? fmtDateTime(g.last_activity) : "never"}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-[#1d3557] font-semibold">{g.jc_this_week}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {g.sync_events_7d > 0 ? (
                      <span className="text-[#e63946] font-bold">{g.sync_events_7d}</span>
                    ) : (
                      <span className="text-[#a8dadc]">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="inline-flex flex-wrap gap-1.5">
                      {g.suspended && <Flag tone="red" label="Suspended" />}
                      {g.dormant_30d && <Flag tone="red" label="Dormant" />}
                      {g.stale && !g.dormant_30d && <Flag tone="orange" label="Stale" />}
                      {g.sync_events_7d > 0 && <Flag tone="orange" label="Sync events" />}
                      {!g.suspended && !g.dormant_30d && !g.stale && g.sync_events_7d === 0 && (
                        <Flag tone="green" label="Healthy" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "events" && (
        <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-sync-events-table">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">When</th>
                <th className="text-left px-4 py-3 font-semibold">Garage</th>
                <th className="text-left px-4 py-3 font-semibold">User</th>
                <th className="text-left px-4 py-3 font-semibold">Kind</th>
                <th className="text-left px-4 py-3 font-semibold">Detail</th>
                <th className="text-right px-4 py-3 font-semibold">Pending</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1FAEE]">
              {loading && <tr><td colSpan={6} className="px-4 py-12 text-center text-[13px] text-[#667085]">Loading…</td></tr>}
              {!loading && filteredEvents.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-[13px] text-[#667085]">
                  No sync events yet. PWA clients POST here when their offline queue can&apos;t drain.
                </td></tr>
              )}
              {filteredEvents.map((e) => (
                <tr key={e.id} className="hover:bg-[#F7F9FC]" data-testid={`admin-sync-event-row-${e.id}`}>
                  <td className="px-4 py-3 text-[11px] font-mono text-[#667085] whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                  <td className="px-4 py-3">
                    {e.garage_id ? (
                      <Link to={`/admin/garages/${e.garage_id}`} className="text-[12px] font-semibold text-[#1d3557] hover:text-[#e63946]">
                        {e.garage_name || e.garage_id.slice(0, 8)}
                      </Link>
                    ) : <span className="text-[12px] text-[#a8dadc]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-[#667085] font-mono">{e.user_email || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#e63946]/10 text-[#e63946]">
                      {e.kind}
                    </span>
                    {e.module && (
                      <div className="text-[10px] text-[#667085] uppercase tracking-wider mt-0.5 font-semibold">{e.module}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-[#667085] max-w-md truncate" title={e.detail}>{e.detail || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#e63946] font-semibold">{e.pending_count ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Flag = ({ label, tone }) => {
  const map = {
    red: "bg-[#e63946]/10 text-[#e63946]",
    orange: "bg-[#F79009]/10 text-[#F79009]",
    green: "bg-[#12B76A]/10 text-[#12B76A]",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[tone]}`}>
      {label}
    </span>
  );
};
